import { type ReactElement } from 'react';
import { mapChipKeyToStatusTone } from '@/features/shared/utils/tableMetricPresentation';
import { typographyTokens } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { formatDuration, formatFrequency, formatPercent } from '@/ui';
import { getThresholdChipKey } from '@/ui/composites/table';
import { Box, Text } from '@/ui/primitives';
import type { MetricThreshold } from '@/shared/utils/metricsThresholds';
import type { SemanticColorKey } from '@/theme/semanticTokens';
import type { StatusTone } from '@/ui/primitives/Chip';

export type TableV2MetricChipKind = 'avgDuration' | 'flakeScore' | 'frequency' | 'passRate';

type TableV2MetricChipProps = {
  kind: TableV2MetricChipKind;
  threshold: MetricThreshold;
  value: number | null | undefined;
};

type TableV2MetricValueProps = {
  accentWidth?: string;
  label: string;
  tone: ReturnType<typeof mapChipKeyToStatusTone> | StatusTone;
  valueColor?: SemanticColorKey;
  variant?: 'detail' | 'table';
};

const tableV2MetricValueGap = '10px';

const resolveTableV2MetricValueTone = (
  tone: ReturnType<typeof mapChipKeyToStatusTone> | StatusTone,
): ReturnType<typeof mapChipKeyToStatusTone> => {
  if (tone === 'success' || tone === 'green') return 'green';
  if (tone === 'error' || tone === 'red') return 'red';
  if (tone === 'warning' || tone === 'orange' || tone === 'yellow') return 'orange';
  if (tone === 'info' || tone === 'blue') return 'blue';
  if (tone === 'primary' || tone === 'teal') return 'teal';
  if (tone === 'purple') return 'purple';
  if (tone === 'brown') return 'brown';
  return 'neutral';
};

const formatTableV2MetricChipValue = (
  kind: TableV2MetricChipKind,
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) return '-';
  if (kind === 'avgDuration') return formatDuration(value);
  if (kind === 'frequency') return formatFrequency(value);
  return `${formatPercent(value)}%`;
};

const getTableV2MetricThresholdValue = (
  kind: TableV2MetricChipKind,
  value: number | null | undefined,
): number | null | undefined => {
  if (kind === 'avgDuration' && value !== null && value !== undefined) return value / 1000;
  return value;
};

export const TableV2MetricValue = ({
  accentWidth = '2px',
  label,
  tone,
  valueColor,
  variant = 'table',
}: TableV2MetricValueProps): ReactElement => {
  if (label === '-') {
    return (
      <Text
        role="body"
        component="span"
        color={variant === 'detail' ? 'textPrimary' : 'textSecondary'}
      >
        -
      </Text>
    );
  }

  const metricTone = resolveTableV2MetricValueTone(tone);
  const chipColor =
    metricTone === 'neutral' ? uiTokens.statusColors.neutral : uiTokens.statusColors[metricTone];

  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        display: 'inline-flex',
        height: variant === 'detail' ? 'auto' : '28px',
        maxWidth: '100%',
        minWidth: 0,
        pl: tableV2MetricValueGap,
        position: 'relative',
        '&::before': {
          backgroundColor: chipColor,
          content: '""',
          height: variant === 'detail' ? '18px' : '100%',
          left: 0,
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          width: accentWidth,
        },
      }}
    >
      <Text
        role="body"
        component="span"
        color={valueColor ?? (variant === 'detail' ? 'textPrimary' : 'textSecondary')}
        sx={[
          {
            fontVariantNumeric: 'tabular-nums',
            fontWeight: typographyTokens.body.fontWeight,
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          variant === 'table' ? { fontSize: typographyTokens.captionSmall.fontSize } : {},
        ]}
      >
        {label}
      </Text>
    </Box>
  );
};

export const TableV2MetricChip = ({
  kind,
  threshold,
  value,
}: TableV2MetricChipProps): ReactElement => {
  if (value === null || value === undefined) {
    return (
      <Text role="body" component="span" color="textSecondary">
        -
      </Text>
    );
  }

  const thresholdValue = getTableV2MetricThresholdValue(kind, value);
  const tone = mapChipKeyToStatusTone(getThresholdChipKey(thresholdValue, threshold));

  return <TableV2MetricValue label={formatTableV2MetricChipValue(kind, value)} tone={tone} />;
};
